import React, { useState, useEffect } from 'react';
import { apiClient } from '../utils/api';
import { WorkflowTagMetrics } from '../types/analytics';
import toast from 'react-hot-toast';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Plus } from 'lucide-react';

/**
 * AdminMetrics Page
 *
 * Platform admin-only page for managing workflow tag time savings metrics.
 * Features:
 * - List all workflow tag metrics
 * - Create new tag metrics
 * - Update existing tag metrics (inline editing)
 * - Delete tag metrics
 * - Input validation (0-480 minutes)
 */
export const AdminMetrics: React.FC = () => {
  const [metrics, setMetrics] = useState<WorkflowTagMetrics[]>([]);
  const [loading, setLoading] = useState(true);
  const [editingTag, setEditingTag] = useState<string | null>(null);
  const [formData, setFormData] = useState({
    tag_name: '',
    estimated_time_saved_minutes: 0,
    hourly_rate_euros: 0,
    cost_per_execution: 0,
  });

  useEffect(() => {
    fetchMetrics();
  }, []);

  const fetchMetrics = async () => {
    try {
      setLoading(true);
      const data = await apiClient.getWorkflowTagMetrics();
      setMetrics(data);
    } catch (error) {
      toast.error('Failed to load metrics');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (metric: WorkflowTagMetrics) => {
    setEditingTag(metric.tag_name);
    setFormData({
      tag_name: metric.tag_name,
      estimated_time_saved_minutes: metric.estimated_time_saved_minutes,
      hourly_rate_euros: metric.hourly_rate_euros || 0,
      cost_per_execution: metric.cost_per_execution || 0,
    });
  };

  const handleCreate = () => {
    setEditingTag('NEW');
    setFormData({
      tag_name: '',
      estimated_time_saved_minutes: 0,
      hourly_rate_euros: 0,
      cost_per_execution: 0,
    });
  };

  const handleSave = async () => {
    try {
      // Validation
      if (formData.estimated_time_saved_minutes < 0 || formData.estimated_time_saved_minutes > 480) {
        toast.error('Time saved must be between 0 and 480 minutes');
        return;
      }

      if (formData.hourly_rate_euros < 0 || formData.hourly_rate_euros > 10000) {
        toast.error('Hourly rate must be between 0 and 10000 euros');
        return;
      }

      if (!formData.tag_name.trim()) {
        toast.error('Tag name is required');
        return;
      }

      // Prepare data - only include optional fields if they have valid values
      const requestData: {
        estimated_time_saved_minutes: number;
        hourly_rate_euros?: number;
        cost_per_execution?: number;
      } = {
        estimated_time_saved_minutes: formData.estimated_time_saved_minutes,
      };

      // Only include hourly_rate_euros if it's a valid number > 0
      if (formData.hourly_rate_euros && !isNaN(formData.hourly_rate_euros) && formData.hourly_rate_euros > 0) {
        requestData.hourly_rate_euros = formData.hourly_rate_euros;
      }

      // Only include cost_per_execution if it's a valid number > 0
      if (formData.cost_per_execution && !isNaN(formData.cost_per_execution) && formData.cost_per_execution > 0) {
        requestData.cost_per_execution = formData.cost_per_execution;
      }

      await apiClient.upsertWorkflowTagMetrics(formData.tag_name, requestData);

      toast.success('Metrics saved successfully');
      setEditingTag(null);
      fetchMetrics();
    } catch (error) {
      toast.error('Failed to save metrics');
      console.error(error);
    }
  };

  const handleDelete = async (tagName: string) => {
    if (!confirm(`Delete metrics for tag "${tagName}"?`)) {
      return;
    }

    try {
      await apiClient.deleteWorkflowTagMetrics(tagName);
      toast.success('Metrics deleted');
      fetchMetrics();
    } catch (error) {
      toast.error('Failed to delete metrics');
      console.error(error);
    }
  };

  const handleCancel = () => {
    setEditingTag(null);
  };

  if (loading) {
    return (
      <div className="p-8">
        <div className="text-muted-foreground">Loading metrics...</div>
      </div>
    );
  }

  return (
    <div className="p-8">
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-3xl font-bold text-foreground">Workflow Tag Metrics</h1>
        <Button onClick={handleCreate}>
          <Plus className="w-4 h-4 mr-2" />
          New Tag Metric
        </Button>
      </div>

      <Card className="overflow-hidden">
        <table className="min-w-full divide-y divide-border">
          <thead className="bg-muted/50">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase">
                Tag Name
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase">
                Time Saved (min)
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase">
                Hourly Rate (€/h)
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase">
                Kosten/Execution (€)
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase">
                Updated
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase">
                Actions
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-border">
            {editingTag === 'NEW' && (
              <tr className="bg-muted/30">
                <td className="px-6 py-4">
                  <Input
                    type="text"
                    value={formData.tag_name}
                    onChange={(e) => setFormData({ ...formData, tag_name: e.target.value })}
                    className="w-full"
                    placeholder="e.g. coldmail"
                  />
                </td>
                <td className="px-6 py-4">
                  <Input
                    type="number"
                    min="0"
                    max="480"
                    value={formData.estimated_time_saved_minutes}
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        estimated_time_saved_minutes: parseInt(e.target.value) || 0,
                      })
                    }
                    className="w-24"
                  />
                </td>
                <td className="px-6 py-4">
                  <Input
                    type="number"
                    min="0"
                    max="10000"
                    step="0.01"
                    value={formData.hourly_rate_euros}
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        hourly_rate_euros: parseFloat(e.target.value) || 0,
                      })
                    }
                    className="w-32"
                    placeholder="e.g. 50.00"
                  />
                </td>
                <td className="px-6 py-4">
                  <Input
                    type="number"
                    min="0"
                    max="10000"
                    step="0.001"
                    value={formData.cost_per_execution}
                    onChange={(e) => setFormData({ ...formData, cost_per_execution: parseFloat(e.target.value) || 0 })}
                    className="w-32"
                    placeholder="e.g. 0.013"
                  />
                </td>
                <td className="px-6 py-4 text-muted-foreground">-</td>
                <td className="px-6 py-4">
                  <Button variant="ghost" size="sm" onClick={handleSave} className="text-primary mr-2">
                    Save
                  </Button>
                  <Button variant="ghost" size="sm" onClick={handleCancel} className="text-muted-foreground">
                    Cancel
                  </Button>
                </td>
              </tr>
            )}
            {metrics.map((metric) => (
              <tr key={metric.id} className={editingTag === metric.tag_name ? 'bg-muted/30' : 'hover:bg-muted/50'}>
                {editingTag === metric.tag_name ? (
                  <>
                    <td className="px-6 py-4 font-medium text-foreground">{metric.tag_name}</td>
                    <td className="px-6 py-4">
                      <Input
                        type="number"
                        min="0"
                        max="480"
                        value={formData.estimated_time_saved_minutes}
                        onChange={(e) =>
                          setFormData({
                            ...formData,
                            estimated_time_saved_minutes: parseInt(e.target.value) || 0,
                          })
                        }
                        className="w-24"
                      />
                    </td>
                    <td className="px-6 py-4">
                      <Input
                        type="number"
                        min="0"
                        max="10000"
                        step="0.01"
                        value={formData.hourly_rate_euros}
                        onChange={(e) =>
                          setFormData({
                            ...formData,
                            hourly_rate_euros: parseFloat(e.target.value) || 0,
                          })
                        }
                        className="w-32"
                      />
                    </td>
                    <td className="px-6 py-4">
                      <Input
                        type="number"
                        min="0"
                        max="10000"
                        step="0.001"
                        value={formData.cost_per_execution}
                        onChange={(e) => setFormData({ ...formData, cost_per_execution: parseFloat(e.target.value) || 0 })}
                        className="w-32"
                      />
                    </td>
                    <td className="px-6 py-4 text-sm text-muted-foreground">
                      {new Date(metric.updated_at).toLocaleDateString()}
                    </td>
                    <td className="px-6 py-4">
                      <Button variant="ghost" size="sm" onClick={handleSave} className="text-primary mr-2">
                        Save
                      </Button>
                      <Button variant="ghost" size="sm" onClick={handleCancel} className="text-muted-foreground">
                        Cancel
                      </Button>
                    </td>
                  </>
                ) : (
                  <>
                    <td className="px-6 py-4 font-medium text-foreground">{metric.tag_name}</td>
                    <td className="px-6 py-4 text-foreground">{metric.estimated_time_saved_minutes}</td>
                    <td className="px-6 py-4 text-foreground">
                      {metric.hourly_rate_euros !== null
                        ? `€${Number(metric.hourly_rate_euros).toFixed(2)}`
                        : '-'
                      }
                    </td>
                    <td className="px-6 py-4 text-muted-foreground text-sm">
                      {metric.cost_per_execution !== null
                        ? `€${Number(metric.cost_per_execution).toFixed(3)}`
                        : '-'
                      }
                    </td>
                    <td className="px-6 py-4 text-sm text-muted-foreground">
                      {new Date(metric.updated_at).toLocaleDateString()}
                    </td>
                    <td className="px-6 py-4">
                      <Button variant="ghost" size="sm" onClick={() => handleEdit(metric)} className="text-primary mr-2">
                        Edit
                      </Button>
                      <Button variant="ghost" size="sm" onClick={() => handleDelete(metric.tag_name)} className="text-destructive">
                        Delete
                      </Button>
                    </td>
                  </>
                )}
              </tr>
            ))}
          </tbody>
        </table>

        {metrics.length === 0 && editingTag !== 'NEW' && (
          <div className="p-8 text-center text-muted-foreground">
            No workflow tag metrics configured yet. Click "New Tag Metric" to add one.
          </div>
        )}
      </Card>
    </div>
  );
};
